import { mergeWith } from 'lodash';
import { BehaviorSubject, EMPTY, Observable, Subscription, combineLatest, of, zip } from 'rxjs';
import { catchError, debounceTime, mergeMap } from 'rxjs/operators';

import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, FormGroup, FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { Contact, CrudItem, Order, OrderPosition, TaxonomyValues, WidgetCallbackRequest } from '@ls-front/sharable';
import { Locker, LsStorage, MessengerService, PhoneMaskService } from '@ls-core';
import { WidgetBase, WidgetChecklist, WidgetGroup, WidgetSelect } from '@ls-custom-ui/widget/field';
import { PublicNomenclaturePositionService } from '@ls-features/nomenclature/services/public-nomenclature-position.service';
import { PublicOrderService } from '@ls-features/order/services/public-order.service';
import { ContragentNotificationService } from '@ls-features/contragent/services/contragent-notification.service';
import { CrudOrderPositionService } from '@ls-features-facade/crud/services/crud-order-position.service';
import { ContactService } from '@ls-features/contact/services/contact.service';

/**
 * Part of logic taken from:
 *
 * @see DialogItemEditComponent
 */
@Component({
    selector: 'app-page-public-order-widget',
    templateUrl: './page-public-order-widget.component.html',
    providers: [CrudOrderPositionService],
    styleUrls: ['./page-public-order-widget.component.scss'],
    standalone: false,
})
export class PagePublicOrderWidgetComponent implements OnInit {
    public form: UntypedFormGroup = new UntypedFormGroup({ item: new UntypedFormControl() });
    public contactForm = new FormGroup<{ comment: FormControl<string> }>({
        comment: new FormControl(''),
    });

    private readonly publicOrderPersistKey = 'lisa.public-order-widget.public-order';

    public showContactForm = true;
    public orderData: Order = new Order();
    public values: TaxonomyValues;

    public item: OrderPosition;
    public contractorId: number;

    public widgets: WidgetBase[] = [];

    public nomenclaturePositionSubscription: Subscription;
    public submitSubscription: Subscription;

    public showPhoto = false;

    public isPayable = false;
    public isSharable = false;

    public newUuidValue: string;

    public searchContactPhoneTyped = new BehaviorSubject<string>('');

    public contactPerson = new UntypedFormControl('', Validators.required);
    public contactPhone = new UntypedFormControl('', Validators.required);

    public subjContactItems = new BehaviorSubject<Contact[]>([]);

    public locker = new Locker();

    constructor(
        public crudOrderPosition: CrudOrderPositionService,
        private route: ActivatedRoute,
        private notifier: MessengerService,
        private contragentNotificationService: ContragentNotificationService,
        private publicNomenclaturePositionService: PublicNomenclaturePositionService,
        private publicOrderService: PublicOrderService,
        public phoneMask: PhoneMaskService,
        private contactService: ContactService
    ) {
        const persistedOrder = LsStorage.get<Order>(this.publicOrderPersistKey);

        if (persistedOrder !== null) {
            try {
                this.orderData = new Order(persistedOrder);
            } catch {
                this.notifier.error('Ошибка при восстановлении заказа');
                LsStorage.remove(this.publicOrderPersistKey);
            }
        }

        this.initBehaviorOfContact();
    }

    public ngOnInit(): void {
        const uuid = this.route.snapshot.params['nom-position-uuid'];
        const externalDataContractor = this.route.snapshot.queryParams['externalDataContractor'];
        this.contractorId = Number(this.route.snapshot.queryParams.contractor);
        this.isPayable = Boolean(Number.parseInt(this.route.snapshot.queryParams.pay, 10));
        this.isSharable = Boolean(Number.parseInt(this.route.snapshot.queryParams.share, 10));

        if (externalDataContractor || externalDataContractor === '') {
            const decodedContractor = decodeURIComponent(externalDataContractor);
            this.orderData.externalData = {
                ...this.orderData.externalData,
                contractor: this.orderData.externalData?.contractor ? this.orderData.externalData.contractor : decodedContractor,
            };

            this.showContactForm = false;
        }

        this.nomenclaturePositionSubscription = this.publicNomenclaturePositionService.getNomenclaturePosition(uuid, true).pipe(
            mergeMap(nomPosition => {
                this.item = new OrderPosition({
                    nomPositionUuid: nomPosition.uuid,
                    nomTypeUuid: nomPosition.nomTypeUuid,
                    qty: nomPosition.qty,
                    contrId: this.contractorId,
                    name: nomPosition.nomTypeName,
                    orderId: this.orderData.id,
                });
                this.values = nomPosition.values;
                this.showPhoto = this.values.f_print_photo && !/не/i.test(this.values.f_print_photo);
                return this.buildWidgets(this.item);
            })
        ).subscribe(
            (widgets: WidgetBase[]) => this.widgets = widgets
        );
    }

    public selectContactPhone(phone: string): void {
        this.contactPhone.patchValue(this.phoneMask.unmask(phone));
        const { contactPerson } = this.subjContactItems.getValue().find(contact => contact.contactPhone === phone);
        this.contactPerson.patchValue(contactPerson);
    }

    public selectContactPerson(person: string): void {
        this.contactPerson.patchValue(person);
        const { contactPhone } = this.subjContactItems.getValue().find(contact => contact.contactPerson === person);
        this.contactPhone.patchValue(this.phoneMask.unmask(contactPhone));
    }

    private initBehaviorOfContact(): void {
        /**
         * This method has a bug: https://lisa10.planfix.ru/task/18705
         * It happens due to requests sent via this.contactService.getContactList which requires authorised access.
         * TODO: fix it
         */
        return;
        combineLatest([this.contactPerson.valueChanges, this.contactPhone.valueChanges])
            .pipe(
                debounceTime(500),
                mergeMap(([contactPerson, contactPhone]) => zip(
                    of([contactPerson, contactPhone]),
                    this.contactService.getContactList({
                        contactPhone: contactPhone || '',
                        contactPerson: contactPerson || '',
                    })
                )),
                mergeMap(([[contactPerson, contactPhone], list]) => {
                    this.searchContactPhoneTyped.next(contactPhone);

                    this.subjContactItems.next(list.items);

                    const isValid = (!contactPerson || !contactPhone)
                        ? (contactPerson && this.contactPerson.valid) || (contactPhone && this.contactPhone.valid)
                        : (contactPerson && this.contactPerson.valid) && (contactPhone && this.contactPhone.valid);

                    if (isValid) {
                        const existsContact = list.items
                            .find(contact => contact.contactPhone === (contactPhone || '') && contact.contactPerson === (contactPerson || ''));

                        if (!existsContact || existsContact?.uuid) {
                            return this.contactService.updateContact({
                                uuid: this.newUuidValue || existsContact?.uuid,
                                contactPerson,
                                contactPhone,
                            });
                        }
                    }

                    return of({ uuid: null });
                })
            )
            .subscribe(data => {
                if (data.uuid) {
                    this.newUuidValue = data.uuid;
                    this.subjContactItems.next([...this.subjContactItems.value, data as Contact]);
                }
            });
    }

    public onSubmit(): void {
        if (this.isBusy()) {
            return;
        }

        this.prepareToSubmit();
        this.updateOrder();
    }

    public isBusy(): boolean {
        return (this.submitSubscription && !this.submitSubscription.closed) || (this.crudOrderPosition.locker.isLocked);
    }

    public hasOrders(): boolean {
        return this.orderData.positions.length > 0;
    }

    /**
     * todo: get rid from copy paste
     *
     * @see DialogItemEditComponent.buildWidgets
     */
    protected buildWidgets(item: CrudItem): Observable<WidgetBase[]> {
        return this.crudOrderPosition.crudModalEditBuildWidgets(true, item);
    }

    /**
     * todo: get rid from copy paste
     *
     * @see DialogItemEditComponent.prepareToSubmit
     */
    protected prepareToSubmit(): void {
        const multipleKeys = this.getKeysWithMultipleValues();
        console.log('DialogItemEditComponent.prepareToSubmit >> multipleKeys', multipleKeys);
        mergeWith(this.item, this.form.get('item').value, (objValue, srcValue, key) => {
            if (Array.isArray(srcValue) && multipleKeys.indexOf(key) > -1) {
                return srcValue;
            }
        });
    }

    /**
     * todo: get rid from copy paste
     *
     * @see DialogItemEditComponent.getKeysWithMultipleValues
     */
    private getKeysWithMultipleValues(): string[] {
        const keys: string[] = [];
        const getKeys: (widgets: WidgetBase[]) => void = widgets => {
            widgets.forEach((widget: WidgetBase) => {
                if (widget instanceof WidgetGroup) {
                    getKeys(widget.widgets);
                } else if ((widget instanceof WidgetSelect || widget instanceof WidgetChecklist) && widget.multiple) {
                    keys.push(widget.key);
                }
            });
        };
        getKeys(this.widgets);
        return keys;
    }

    public createOrder(): void {
        this.submitSubscription = this.publicOrderService.createOrder().pipe(
            mergeMap((order: Order) => {
                const updatePayload = {
                    ...order,
                    contrId: this.contractorId,
                    shortNote: 'Виджет',
                    contactPhone: this.contactPhone.value,
                    contactPerson: this.contactPerson.value,
                    comment: this.orderData.comment || this.orderData.externalData?.contractor || '',
                    externalData: this.orderData.externalData || {},
                };

                return this.publicOrderService.updateOrder(updatePayload);
            }),
            mergeMap((updatedOrder: Order) => {
                this.item.orderId = updatedOrder.id;
                return this.publicOrderService.updateOrderPosition(this.item);
            })
        ).subscribe(() => {
            this.getOrder(this.item.orderId);
            this.clearOrderForm();
        });
    }

    public updateOrder(): void {
        if (!this.orderData.id) {
            this.createOrder();
        } else {
            this.publicOrderService.updateOrder({
                ...this.orderData,
                contactPhone: this.contactPhone.value,
                contactPerson: this.contactPerson.value,
            }).pipe(
                mergeMap(() => this.publicOrderService.updateOrderPosition(this.item))
            ).subscribe(() => {
                this.getOrder(this.item.orderId);
                this.clearOrderForm(false);
            });
        }
    }

    public getOrder(orderId: number): void {
        this.publicOrderService.getOrder(orderId).pipe(
            catchError(error => {
                console.log(error);
                return EMPTY;
            }),
            this.locker.rxPipe()
        ).subscribe(order => {
            this.orderData = order;
            this.contactPerson.patchValue(order.contactPerson);
            this.contactPhone.patchValue(order.contactPhone);
            LsStorage.set(this.publicOrderPersistKey, order);
        });
    }

    public clearOrderForm(shouldClearOrder: boolean = true): void {
        this.widgets.forEach(widget => {
            widget.control.reset();
            if (widget instanceof WidgetGroup) {
                widget.widgets.forEach(widgetList => {
                    widgetList.control.reset();
                });
            }
        });

        if (shouldClearOrder) {
            const preservedExternalData = this.orderData.externalData;
            this.orderData = new Order();
            this.orderData.externalData = preservedExternalData;
        }

        LsStorage.remove(this.publicOrderPersistKey);
    }

    public clearContactForm(): void {
        this.contactForm.reset();
        this.contactPerson.reset();
        this.contactPhone.reset();
        this.contactForm.markAsPristine();
    }

    private createComment(): string {
        const formData = this.contactForm.value;
        if (!this.contactPerson.value && !this.contactPhone.value && !formData.comment) {
            return this.orderData.externalData?.contractor;
        }
        return `Имя: ${this.contactPerson.value || '-'}; Телефон: ${this.contactPhone.value || '-'}; Комментарий: ${formData.comment || '-'}`;
        // return `Имя: ${this.orderData.contactPerson || '-'}; Телефон: ${this.orderData.contactPhone || '-'}; Комментарий: ${formData.comment || '-'}`;
    }

    public onSubmitContactForm(): void {
        this.contactForm.markAsDirty();
        if (this.showContactForm && this.hasError()) {
            return;
        }

        this.contragentNotificationService.sendCallbackRequest(new WidgetCallbackRequest({
            orderId: this.orderData.id,
            contractorId: this.contractorId,
            name: this.contactPerson.value,
            phone: this.contactPhone.value,
            comment: this.contactForm.get('comment').value,
            requireContact: this.showContactForm,
        })).pipe(
            mergeMap(() => this.publicOrderService
                .updateOrder({
                    ...this.orderData, contactPerson: this.contactPerson.value, contactPhone: this.contactPhone.value,
                    comment: this.createComment(),
                })),
            catchError(() => EMPTY)
        ).subscribe(() => {
            this.notifier.success('Заявка успешно отправлена');
            this.clearContactForm();
            this.clearOrderForm();
        });
    }

    public hasError(): boolean {
        return this.contactForm.dirty && !this.contactPhone.value;
    }

    public generateLink(): string {
        return `${window.location.origin}/export/public-bill/${this.orderData.id}`;
    }
}
